// Normalización de columnas: tipo, alineación y ancho de cada columna a partir
// de su encabezado, `dataType`, `align` y los valores de la página visible.
import { isValidElement } from "react";
import { sizeDataTableColumns } from "./column-sizing";
import createStatusCell from "./status-cell";

const ALIGNMENT_CLASS = {
  left: "dhn-data-table__cell--left",
  center: "dhn-data-table__cell--center",
  right: "dhn-data-table__cell--right",
};

const ALIGNMENT_BY_KIND = {
  actions: "left",
  boolean: "center",
  code: "center",
  date: "center",
  fiscalNumber: "center",
  taxId: "center",
  money: "right",
  numeric: "right",
  status: "center",
  text: "left",
};

const KIND_BY_DATA_TYPE = {
  actions: "actions",
  action: "actions",
  buttons: "actions",
  text: "text",
  date: "date",
  datetime: "date",
  time: "date",
  status: "status",
  boolean: "boolean",
  code: "code",
  fiscalNumber: "fiscalNumber",
  taxId: "taxId",
  currency: "money",
  money: "money",
  amount: "money",
  quantity: "numeric",
  number: "numeric",
  percentage: "numeric",
};

const TAX_ID_PATTERN = /\b(rnc)\b/;
const ACTION_PATTERN = /\b(opcion|opciones|accion|acciones|action|actions|option|options|boton|botones|control|controles)\b/;
const DATE_PATTERN = /\b(fecha|date|hora|hour|time|periodo|period|vencimiento|expiracion|creado|actualizado|created|updated|createdat|updatedat)\b/;
// Encabezados "Estado ..." / "Estatus ..." (salvo "Estado Civil"): sus celdas
// de texto se muestran con StatusBadge.
const STATUS_HEADER_PATTERN = /^(estado|estatus|status)\b(?! civil)/;
const STATUS_PATTERN = /\b(estado|estatus|status|activo|activa|active|enabled|habilitado|seleccion)\b/;
const CODE_PATTERN = /\b(id|codigo|code|numero|no|nro|referencia|reference)\b/;
const MONEY_PATTERN = /\b(valor|monto|amount|precio|price|costo|cost|total|subtotal|balance|copago|cobertura|coverage|fee|tarifa|impuesto|descuento|discount)\b/;
const QUANTITY_PATTERN = /\b(cantidad|quantity|qty|unidades|units|dosis|dose|minimo|maximo|minimum|maximum|porcentaje|percentage|tasa|rate)\b/;
const FALLBACK_CHARACTER_WIDTH = 7;
// Un e-CF tiene 13 caracteres (E + tipo + secuencia de 10 dígitos); este ancho
// mínimo lo muestra completo en una sola línea junto al relleno de la celda.
export const FISCAL_NUMBER_COLUMN_WIDTH = 140;
// El RNC de una empresa tiene 9 dígitos (1-01-78868-2), pero el campo también
// recibe cédulas de persona física de 11 dígitos (001-1234567-8, 13 caracteres
// con guiones). Este ancho muestra el más largo en una sola línea.
export const TAX_ID_COLUMN_WIDTH = 140;

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .toLowerCase();

const getHeaderText = (header) => {
  if (typeof header === "string" || typeof header === "number") return header;
  if (
    isValidElement(header) &&
    (typeof header.props.children === "string" ||
      typeof header.props.children === "number")
  )
    return header.props.children;

  return "";
};

const getExistingAlignment = (column) => {
  if (ALIGNMENT_CLASS[column.style?.textAlign]) return column.style.textAlign;
  if (typeof column.className !== "string") return null;
  if (/text-right|align-right/.test(column.className)) return "right";
  if (/text-center|align-center/.test(column.className)) return "center";
  if (/text-left|align-left/.test(column.className)) return "left";
  return null;
};

const getColumnKey = (column) =>
  normalizeText(
    [getHeaderText(column.Header), column.id, column.accessor]
      .filter((value) => typeof value === "string" || typeof value === "number")
      .join(" "),
  );

const getColumnKind = (column) => {
  const dataTypeKind = KIND_BY_DATA_TYPE[column.dataType];
  if (dataTypeKind) return dataTypeKind;

  if (
    !column.dataType &&
    STATUS_HEADER_PATTERN.test(normalizeText(getHeaderText(column.Header)))
  )
    return "status";

  const columnKey = getColumnKey(column);

  if (ACTION_PATTERN.test(columnKey)) return "actions";
  if (TAX_ID_PATTERN.test(columnKey)) return "taxId";
  if (MONEY_PATTERN.test(columnKey)) return "money";
  if (QUANTITY_PATTERN.test(columnKey)) return "numeric";
  if (DATE_PATTERN.test(columnKey)) return "date";
  if (CODE_PATTERN.test(columnKey) || STATUS_PATTERN.test(columnKey))
    return "code";

  return "text";
};

const getColumnAlignment = (column) => {
  const dataTypeKind = KIND_BY_DATA_TYPE[column.dataType];
  if (dataTypeKind) return ALIGNMENT_BY_KIND[dataTypeKind];
  if (ALIGNMENT_CLASS[column.align]) return column.align;

  const kind = getColumnKind(column);
  return kind === "text"
    ? getExistingAlignment(column) || ALIGNMENT_BY_KIND.text
    : ALIGNMENT_BY_KIND[kind];
};

const appendClassName = (...classNamesToAppend) =>
  classNamesToAppend.filter(Boolean).join(" ");

const normalizeColumnStyles = (columns = []) =>
  columns.filter(Boolean).map((column) => {
    const normalizedColumn = {
      ...column,
      headerClassName: appendClassName(
        column.headerClassName,
        "dhn-data-table__header",
        Array.isArray(column.columns) && "dhn-data-table__header--group",
      ),
    };

    if (Array.isArray(column.columns)) {
      normalizedColumn.columns = normalizeColumnStyles(column.columns);
      return normalizedColumn;
    }

    if (getColumnKind(column) === "status")
      normalizedColumn.Cell = createStatusCell(column.Cell);

    const alignment = getColumnAlignment(column);
    normalizedColumn.className = appendClassName(
      column.className,
      "dhn-data-table__cell",
      ALIGNMENT_CLASS[alignment],
      getColumnKind(column) === "money" && "dhn-data-table__cell--money",
      ["fiscalNumber", "taxId"].includes(getColumnKind(column)) &&
        "dhn-data-table__cell--fiscal-number",
    );

    return normalizedColumn;
  });

const MIN_WIDTH_BY_KIND = {
  fiscalNumber: FISCAL_NUMBER_COLUMN_WIDTH,
  taxId: TAX_ID_COLUMN_WIDTH,
};

// Los comprobantes fiscales y el RNC parten de un ancho mínimo que los muestra
// en una sola línea y pueden crecer con el espacio disponible, salvo que la
// columna declare su propio `width` o `minWidth`.
const applyMinWidthsByKind = (columns = []) =>
  columns.map((column) => {
    if (!column) return column;
    if (Array.isArray(column.columns))
      return { ...column, columns: applyMinWidthsByKind(column.columns) };

    const minWidth = MIN_WIDTH_BY_KIND[getColumnKind(column)];
    if (
      !minWidth ||
      Number.isFinite(column.width) ||
      Number.isFinite(column.minWidth)
    )
      return column;

    return { ...column, minWidth };
  });

export const fallbackMeasureText = (value) =>
  String(value ?? "").length * FALLBACK_CHARACTER_WIDTH;

export const createTextMeasurer = (font) => {
  if (typeof document === "undefined") return fallbackMeasureText;

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) return fallbackMeasureText;
  context.font = font;

  return (value) => context.measureText(String(value ?? "")).width;
};

export const normalizeDataTableColumns = (
  columns = [],
  data = [],
  { containerWidth = 0, measureText = fallbackMeasureText } = {},
) =>
  normalizeColumnStyles(
    sizeDataTableColumns({
      columns: applyMinWidthsByKind(columns),
      data,
      containerWidth,
      getColumnKind,
      getHeaderText,
      measureText,
    }),
  );
